import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface CommissionRule {
  id: string
  name: string
  description?: string
  commissionType: 'PERCENTAGE' | 'FIXED' | 'TIERED'
  rate?: string
  fixedAmount?: string
  tieredRates?: Array<{ min: number; max?: number; rate: number }>
  salesPerson?: {
    id: string
    firstName: string
    lastName: string
  }
  customer?: {
    id: string
    name: string
  }
  isActive: boolean
  startDate?: string
  endDate?: string
}

interface CommissionEntry {
  id: string
  commissionRule?: CommissionRule
  invoice: {
    id: string
    invoiceNumber: string
    invoiceDate: string
    total: string
    clientOrg: {
      name: string
    }
  }
  salesPerson: {
    id: string
    firstName: string
    lastName: string
  }
  baseAmount: string
  commissionAmount: string
  commissionRate?: string
  currency: string
  commissionDate: string
  commissionPeriod?: string
  status: 'PENDING' | 'APPROVED' | 'PAID' | 'CANCELLED'
  approvedAt?: string
  paidAt?: string
}

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
}

interface ClientOrganization {
  id: string
  name: string
}

const Commissions: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [activeTab, setActiveTab] = useState<'rules' | 'entries' | 'summary'>('rules')
  const [commissionRules, setCommissionRules] = useState<CommissionRule[]>([])
  const [commissionEntries, setCommissionEntries] = useState<CommissionEntry[]>([])
  const [commissionSummary, setCommissionSummary] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showRuleModal, setShowRuleModal] = useState(false)
  const [showCalculateModal, setShowCalculateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedRule, setSelectedRule] = useState<CommissionRule | null>(null)
  const [selectedEntry, setSelectedEntry] = useState<CommissionEntry | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [customers, setCustomers] = useState<ClientOrganization[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    description: '',
    commissionType: 'PERCENTAGE' as const,
    rate: '',
    fixedAmount: '',
    tieredRates: [] as Array<{ min: string; max: string; rate: string }>,
    salesPersonId: '',
    customerId: '',
    startDate: '',
    endDate: '',
    isActive: true,
  })
  const [calculateFormData, setCalculateFormData] = useState({
    invoiceId: '',
    salesPersonId: '',
    commissionDate: new Date().toISOString().split('T')[0],
  })

  useEffect(() => {
    if (activeTab === 'rules') {
      fetchCommissionRules()
    } else if (activeTab === 'entries') {
      fetchCommissionEntries()
    } else {
      fetchCommissionSummary()
    }
  }, [activeTab])

  useEffect(() => {
    fetchUsers()
    fetchCustomers()
  }, [])

  const fetchCommissionRules = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/commission-rules')
      setCommissionRules(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching commission rules:', error)
      toast.error('Failed to load commission rules')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCommissionEntries = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/commission-entries')
      setCommissionEntries(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching commission entries:', error)
      toast.error('Failed to load commission entries')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCommissionSummary = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/commission-entries/summary')
      setCommissionSummary(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching commission summary:', error)
      toast.error('Failed to load commission summary')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users')
      setUsers(response.data.data || [])
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/client-organizations')
      setCustomers(response.data.data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    }
  }

  const fetchInvoices = async () => {
    try {
      const response = await axios.get('/api/erp/invoices')
      setInvoices(response.data.data || [])
    } catch (error) {
      console.error('Error fetching invoices:', error)
    }
  }

  const handleCreateRule = async () => {
    try {
      const payload: any = {
        ...ruleFormData,
        rate: ruleFormData.rate ? parseFloat(ruleFormData.rate) : undefined,
        fixedAmount: ruleFormData.fixedAmount ? parseFloat(ruleFormData.fixedAmount) : undefined,
        tieredRates:
          ruleFormData.commissionType === 'TIERED' && ruleFormData.tieredRates.length > 0
            ? ruleFormData.tieredRates.map((t) => ({
                min: parseFloat(t.min),
                max: t.max ? parseFloat(t.max) : undefined,
                rate: parseFloat(t.rate),
              }))
            : undefined,
        salesPersonId: ruleFormData.salesPersonId || undefined,
        customerId: ruleFormData.customerId || undefined,
        startDate: ruleFormData.startDate || undefined,
        endDate: ruleFormData.endDate || undefined,
      }
      await axios.post('/api/erp/commission-rules', payload)
      toast.success('Commission rule created successfully')
      setShowRuleModal(false)
      resetRuleForm()
      fetchCommissionRules()
    } catch (error: any) {
      console.error('Error creating commission rule:', error)
      toast.error(error.response?.data?.error || 'Failed to create commission rule')
    }
  }

  const resetRuleForm = () => {
    setRuleFormData({
      name: '',
      description: '',
      commissionType: 'PERCENTAGE',
      rate: '',
      fixedAmount: '',
      tieredRates: [],
      salesPersonId: '',
      customerId: '',
      startDate: '',
      endDate: '',
      isActive: true,
    })
  }

  const handleCalculateCommission = async () => {
    try {
      await axios.post('/api/erp/commission-entries/calculate', calculateFormData)
      toast.success('Commission calculated successfully')
      setShowCalculateModal(false)
      setCalculateFormData({
        invoiceId: '',
        salesPersonId: '',
        commissionDate: new Date().toISOString().split('T')[0],
      })
      fetchCommissionEntries()
    } catch (error: any) {
      console.error('Error calculating commission:', error)
      toast.error(error.response?.data?.error || 'Failed to calculate commission')
    }
  }

  const handleApprove = async (id: string) => {
    if (!confirm('Are you sure you want to approve this commission entry?')) return
    try {
      await axios.post(`/api/erp/commission-entries/${id}/approve`)
      toast.success('Commission approved successfully')
      fetchCommissionEntries()
      if (selectedEntry?.id === id) {
        const response = await axios.get(`/api/erp/commission-entries/${id}`)
        setSelectedEntry(response.data.data)
      }
    } catch (error: any) {
      console.error('Error approving commission:', error)
      toast.error(error.response?.data?.error || 'Failed to approve commission')
    }
  }

  const handleMarkAsPaid = async (id: string) => {
    if (!confirm('Are you sure you want to mark this commission as paid?')) return
    try {
      await axios.post(`/api/erp/commission-entries/${id}/mark-paid`)
      toast.success('Commission marked as paid')
      fetchCommissionEntries()
      if (selectedEntry?.id === id) {
        const response = await axios.get(`/api/erp/commission-entries/${id}`)
        setSelectedEntry(response.data.data)
      }
    } catch (error: any) {
      console.error('Error marking commission as paid:', error)
      toast.error(error.response?.data?.error || 'Failed to mark commission as paid')
    }
  }

  const handleCancel = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this commission entry?')) return
    try {
      await axios.post(`/api/erp/commission-entries/${id}/cancel`)
      toast.success('Commission cancelled successfully')
      fetchCommissionEntries()
      if (selectedEntry?.id === id) {
        setShowViewModal(false)
        setSelectedEntry(null)
      }
    } catch (error: any) {
      console.error('Error cancelling commission:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel commission')
    }
  }

  const addTieredRate = () => {
    setRuleFormData({
      ...ruleFormData,
      tieredRates: [...ruleFormData.tieredRates, { min: '', max: '', rate: '' }],
    })
  }

  const removeTieredRate = (index: number) => {
    setRuleFormData({
      ...ruleFormData,
      tieredRates: ruleFormData.tieredRates.filter((_, i) => i !== index),
    })
  }

  const updateTieredRate = (index: number, field: 'min' | 'max' | 'rate', value: string) => {
    const updated = [...ruleFormData.tieredRates]
    updated[index] = { ...updated[index], [field]: value }
    setRuleFormData({ ...ruleFormData, tieredRates: updated })
  }

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>
  }

  if (!hasPermission('erp_commission', 'read')) {
    return <div className="p-6">You don't have permission to access this page.</div>
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Commission Management</h1>
        <PageHelp articleKey="erp_commission" />
      </div>

      {/* Tabs */}
      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('rules')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'rules'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Commission Rules
          </button>
          <button
            onClick={() => setActiveTab('entries')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'entries'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Commission Entries
          </button>
          <button
            onClick={() => setActiveTab('summary')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'summary'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Summary
          </button>
        </nav>
      </div>

      {/* Commission Rules Tab */}
      {activeTab === 'rules' && (
        <div>
          <div className="mb-4 flex justify-end">
            {hasPermission('erp_commission', 'write') && (
              <button
                onClick={() => {
                  setSelectedRule(null)
                  resetRuleForm()
                  setShowRuleModal(true)
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Create Commission Rule
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading commission rules...</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate/Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sales Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissionRules.map((rule) => (
                    <tr key={rule.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {rule.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rule.commissionType}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rule.commissionType === 'PERCENTAGE' && rule.rate
                          ? `${rule.rate}%`
                          : rule.commissionType === 'FIXED' && rule.fixedAmount
                          ? `£${parseFloat(rule.fixedAmount).toFixed(2)}`
                          : rule.commissionType === 'TIERED'
                          ? 'Tiered'
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rule.salesPerson
                          ? `${rule.salesPerson.firstName} ${rule.salesPerson.lastName}`
                          : 'All'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {rule.customer ? rule.customer.name : 'All'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            rule.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {rule.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedRule(rule)
                            setShowViewModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {commissionRules.length === 0 && (
                <div className="text-center py-8 text-gray-500">No commission rules found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Commission Entries Tab */}
      {activeTab === 'entries' && (
        <div>
          <div className="mb-4 flex justify-end gap-2">
            {hasPermission('erp_commission', 'write') && (
              <>
                <button
                  onClick={() => {
                    fetchInvoices()
                    setShowCalculateModal(true)
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Calculate Commission
                </button>
              </>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading commission entries...</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sales Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Base Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissionEntries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.salesPerson.firstName} {entry.salesPerson.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.currency} {parseFloat(entry.baseAmount).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.currency} {parseFloat(entry.commissionAmount).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {entry.commissionRate ? `${entry.commissionRate}%` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(entry.commissionDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            entry.status === 'PAID'
                              ? 'bg-green-100 text-green-800'
                              : entry.status === 'APPROVED'
                              ? 'bg-blue-100 text-blue-800'
                              : entry.status === 'PENDING'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedEntry(entry)
                            setShowViewModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900 mr-3"
                        >
                          View
                        </button>
                        {entry.status === 'PENDING' && hasPermission('erp_commission', 'write') && (
                          <button
                            onClick={() => handleApprove(entry.id)}
                            className="text-green-600 hover:text-green-900 mr-3"
                          >
                            Approve
                          </button>
                        )}
                        {entry.status === 'APPROVED' && hasPermission('erp_commission', 'write') && (
                          <button
                            onClick={() => handleMarkAsPaid(entry.id)}
                            className="text-purple-600 hover:text-purple-900 mr-3"
                          >
                            Mark Paid
                          </button>
                        )}
                        {(entry.status === 'PENDING' || entry.status === 'APPROVED') &&
                          hasPermission('erp_commission', 'write') && (
                            <button
                              onClick={() => handleCancel(entry.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Cancel
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {commissionEntries.length === 0 && (
                <div className="text-center py-8 text-gray-500">No commission entries found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <div>
          {isLoading ? (
            <div className="text-center py-8">Loading commission summary...</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sales Person
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Base Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Commission
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pending
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Approved
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Paid
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entries
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissionSummary.map((summary: any, index: number) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {summary.salesPerson.firstName} {summary.salesPerson.lastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        £{parseFloat(summary.totalBaseAmount).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        £{parseFloat(summary.totalCommission).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600">
                        £{parseFloat(summary.pendingCommission).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                        £{parseFloat(summary.approvedCommission).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        £{parseFloat(summary.paidCommission).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {summary.entryCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {commissionSummary.length === 0 && (
                <div className="text-center py-8 text-gray-500">No commission data found</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create Commission Rule Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Commission Rule</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={ruleFormData.name}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={ruleFormData.commissionType}
                    onChange={(e) =>
                      setRuleFormData({
                        ...ruleFormData,
                        commissionType: e.target.value as any,
                        tieredRates: e.target.value === 'TIERED' ? [] : ruleFormData.tieredRates,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="PERCENTAGE">Percentage</option>
                    <option value="FIXED">Fixed Amount</option>
                    <option value="TIERED">Tiered</option>
                  </select>
                </div>
                {ruleFormData.commissionType === 'PERCENTAGE' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate (%) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={ruleFormData.rate}
                      onChange={(e) => setRuleFormData({ ...ruleFormData, rate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                )}
                {ruleFormData.commissionType === 'FIXED' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={ruleFormData.fixedAmount}
                      onChange={(e) =>
                        setRuleFormData({ ...ruleFormData, fixedAmount: e.target.value })
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sales Person</label>
                  <select
                    value={ruleFormData.salesPersonId}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, salesPersonId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">All Sales Persons</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                  <select
                    value={ruleFormData.customerId}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, customerId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">All Customers</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={ruleFormData.startDate}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={ruleFormData.endDate}
                    onChange={(e) => setRuleFormData({ ...ruleFormData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              {ruleFormData.commissionType === 'TIERED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tiered Rates</label>
                  <div className="space-y-2">
                    {ruleFormData.tieredRates.map((tier, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="number"
                          placeholder="Min"
                          value={tier.min}
                          onChange={(e) => updateTieredRate(index, 'min', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <input
                          type="number"
                          placeholder="Max (optional)"
                          value={tier.max}
                          onChange={(e) => updateTieredRate(index, 'max', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Rate %"
                          value={tier.rate}
                          onChange={(e) => updateTieredRate(index, 'rate', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                        />
                        <button
                          onClick={() => removeTieredRate(index)}
                          className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={addTieredRate}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    >
                      Add Tier
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={ruleFormData.description}
                  onChange={(e) => setRuleFormData({ ...ruleFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={ruleFormData.isActive}
                  onChange={(e) => setRuleFormData({ ...ruleFormData, isActive: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700">Active</label>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowRuleModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRule}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Calculate Commission Modal */}
      {showCalculateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <h2 className="text-xl font-bold mb-4">Calculate Commission</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice *</label>
                <select
                  value={calculateFormData.invoiceId}
                  onChange={(e) =>
                    setCalculateFormData({ ...calculateFormData, invoiceId: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Invoice</option>
                  {invoices
                    .filter((inv) => inv.status === 'POSTED' || inv.status === 'PAID')
                    .map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoiceNumber} - {inv.clientOrg?.name} - £{parseFloat(inv.total).toFixed(2)}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Person *</label>
                <select
                  value={calculateFormData.salesPersonId}
                  onChange={(e) =>
                    setCalculateFormData({ ...calculateFormData, salesPersonId: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Sales Person</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commission Date</label>
                <input
                  type="date"
                  value={calculateFormData.commissionDate}
                  onChange={(e) =>
                    setCalculateFormData({ ...calculateFormData, commissionDate: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={() => setShowCalculateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCalculateCommission}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Calculate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && (selectedRule || selectedEntry) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {selectedRule && (
              <>
                <h2 className="text-xl font-bold mb-4">Commission Rule Details</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        {selectedRule.name}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        {selectedRule.commissionType}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rate/Amount</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        {selectedRule.commissionType === 'PERCENTAGE' && selectedRule.rate
                          ? `${selectedRule.rate}%`
                          : selectedRule.commissionType === 'FIXED' && selectedRule.fixedAmount
                          ? `£${parseFloat(selectedRule.fixedAmount).toFixed(2)}`
                          : selectedRule.commissionType === 'TIERED'
                          ? 'Tiered Rates'
                          : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            selectedRule.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {selectedRule.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedRule.description && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        {selectedRule.description}
                      </div>
                    </div>
                  )}
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setShowViewModal(false)
                        setSelectedRule(null)
                      }}
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            )}
            {selectedEntry && (
              <>
                <h2 className="text-xl font-bold mb-4">Commission Entry Details</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Invoice</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        {selectedEntry.invoice.invoiceNumber} - {selectedEntry.invoice.clientOrg.name}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sales Person</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        {selectedEntry.salesPerson.firstName} {selectedEntry.salesPerson.lastName}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Base Amount</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        {selectedEntry.currency} {parseFloat(selectedEntry.baseAmount).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Commission Amount</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md font-semibold">
                        {selectedEntry.currency} {parseFloat(selectedEntry.commissionAmount).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Rate</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        {selectedEntry.commissionRate ? `${selectedEntry.commissionRate}%` : '-'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            selectedEntry.status === 'PAID'
                              ? 'bg-green-100 text-green-800'
                              : selectedEntry.status === 'APPROVED'
                              ? 'bg-blue-100 text-blue-800'
                              : selectedEntry.status === 'PENDING'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {selectedEntry.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    {selectedEntry.status === 'PENDING' && hasPermission('erp_commission', 'write') && (
                      <button
                        onClick={() => handleApprove(selectedEntry.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Approve
                      </button>
                    )}
                    {selectedEntry.status === 'APPROVED' && hasPermission('erp_commission', 'write') && (
                      <button
                        onClick={() => handleMarkAsPaid(selectedEntry.id)}
                        className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        Mark Paid
                      </button>
                    )}
                    {(selectedEntry.status === 'PENDING' || selectedEntry.status === 'APPROVED') &&
                      hasPermission('erp_commission', 'write') && (
                        <button
                          onClick={() => handleCancel(selectedEntry.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          Cancel
                        </button>
                      )}
                    <button
                      onClick={() => {
                        setShowViewModal(false)
                        setSelectedRule(null)
                        setSelectedEntry(null)
                      }}
                      className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Commissions
